import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHeader, LoadingSkeleton, EmptyState } from "@/components/ui-system";
import { Globe, FileText, ScrollText } from "lucide-react";
import { toast } from "sonner";

const ZONES = ["N1", "N2", "E", "NE", "W1", "W2", "S1", "S2", "Central"];
const OVERHEAD_FIELDS = [
  ["fsc_percent", "Fuel surcharge %"], ["min_charge", "Min LR charge ₹"], ["min_charged_wt", "Min charged wt (kg)"],
  ["volumetric_divisor", "Volumetric divisor"], ["docket_charge", "Processing ₹/LR"], ["gst_percent", "GST %"],
  ["oda_per_kg", "ODA ₹/kg"], ["oda_min", "ODA min ₹"],
  ["fm_per_kg", "First mile ₹/kg"], ["lm_per_kg", "Last mile ₹/kg"],
  ["green_tax_per_kg", "Green tax ₹/kg"], ["green_tax_min", "Green tax min ₹"],
  ["appointment_per_kg", "Appointment ₹/kg"], ["appointment_min", "Appointment min ₹"],
  ["demurrage_per_kg", "Demurrage ₹/kg"], ["demurrage_min", "Demurrage min ₹"],
  ["free_storage_days", "Free storage days"], ["floor_delivery_per_kg", "Floor delivery ₹/kg"],
];

// Read-only 9×9 SELL rate grid (origin ↓ × destination →), ₹/kg.
const ZoneMatrixTable = ({ matrix }) => (
  <div className="overflow-x-auto border rounded-lg">
    <table className="text-xs border-collapse">
      <thead>
        <tr>
          <th className="p-1.5 sticky left-0 bg-white"></th>
          {ZONES.map((d) => <th key={d} className="p-1.5 font-semibold text-slate-500">{d}</th>)}
        </tr>
      </thead>
      <tbody>
        {ZONES.map((o) => (
          <tr key={o}>
            <td className="p-1.5 font-semibold text-slate-500 sticky left-0 bg-white">{o}</td>
            {ZONES.map((d) => {
              const val = matrix?.[o]?.[d];
              return (
                <td key={d} className="p-0.5">
                  <div className={`w-14 px-1 py-1 text-right rounded border ${val == null ? "border-slate-100 bg-slate-50 text-slate-300" : "border-slate-200 bg-white text-slate-700"}`}>
                    {val == null ? "—" : val}
                  </div>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Seller-facing B2B Rate Card. Shows the seller's OWN agreed SELL rates (zone matrix +
// overheads) from their signed agreement — NO carrier cost is ever exposed. The zone
// structure (states per zone) is shown below as reference.
const B2BRateCard = ({ user, onLogout }) => {
  const [zones, setZones] = useState([]);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cardLoading, setCardLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/rate-validation/zone-definition`)
      .then((r) => setZones(r.data.zones || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load zone matrix"))
      .finally(() => setLoading(false));
    axios.get(`${API}/seller-rate-cards/my-card`)
      .then((r) => setCard(r.data?.card || null))
      .catch(() => setCard(null))
      .finally(() => setCardLoading(false));
  }, []);

  const overheads = card?.overheads || {};
  const shownOverheads = OVERHEAD_FIELDS.filter(([k]) => overheads[k] != null);

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader
          title="B2B Rate Card"
          subtitle="Your agreed B2B freight rates — the per-zone ₹/kg and charges from your signed agreement."
        />

        {/* The seller's own SELL rate card, or a placeholder until one is published */}
        {cardLoading ? (
          <LoadingSkeleton />
        ) : !card ? (
          <Card className="border border-dashed border-[#00A699] bg-[#00A699]/5 shadow-none">
            <CardContent className="flex items-start gap-3 py-4">
              <FileText className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "#00A699" }} />
              <div className="text-sm text-slate-600">
                <div className="font-medium text-slate-800">Your rate card is coming soon</div>
                Your signed B2B agreement and the per-zone pricing it defines will appear here once it's been published. For now, this page shows the zone structure your lanes are built on.
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-base">
                <span className="flex items-center gap-2">
                  <ScrollText className="w-4 h-4" style={{ color: "#00A699" }} />
                  {card.title || "Your rate card"}
                </span>
                <Badge variant="outline" className="text-xs">Effective {card.effective_from}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <div className="text-sm font-medium text-slate-700 mb-1">Freight — ₹/kg (origin ↓ × destination →)</div>
                <ZoneMatrixTable matrix={card.zone_matrix} />
              </div>

              {shownOverheads.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-slate-700 mb-2">Charges &amp; parameters</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {shownOverheads.map(([key, label]) => (
                      <div key={key} className="rounded-lg border bg-slate-50 px-3 py-2">
                        <div className="text-[11px] text-slate-500">{label}</div>
                        <div className="text-sm font-medium text-slate-800">{overheads[key]}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(card.location_flat_charges) && card.location_flat_charges.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-slate-700 mb-1">Flat location charges</div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {card.location_flat_charges.map((l, i) => (
                      <span key={i} className="rounded border px-2 py-1 bg-slate-50 text-slate-600">{l.locations}: ₹{l.amount}/{l.per || "GR"}</span>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(card.extra?.lane_surcharges) && card.extra.lane_surcharges.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-slate-700 mb-1">Additive lane surcharges</div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {card.extra.lane_surcharges.map((s, i) => (
                      <span key={i} className="rounded border px-2 py-1 bg-slate-50 text-slate-600">{s.match}: +₹{s.add_per_kg}/kg</span>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-slate-400">
                Rates are exclusive of GST. Fuel surcharge and conditional charges apply per the agreement.
              </p>
            </CardContent>
          </Card>
        )}

        <div>
          <div className="text-sm font-medium text-slate-700 mb-3">Shipping zones &amp; their states</div>
          {loading ? (
            <LoadingSkeleton />
          ) : zones.length === 0 ? (
            <EmptyState icon={Globe} title="No zones" description="Zone matrix is unavailable right now." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {zones.map((z) => (
                <Card key={z.zone} className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between text-base">
                      <span className="flex items-center gap-2">
                        <Globe className="w-4 h-4" style={{ color: "#00A699" }} />
                        {z.label}
                      </span>
                      <Badge className="bg-[#00A699] bg-opacity-10 text-[#00A699] border border-[#00A699]">{z.zone}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1.5">
                      {z.states.map((s) => (
                        <Badge key={s} variant="outline" className="text-xs font-normal">{s}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </SellerLayout>
  );
};

export default B2BRateCard;
